import React, { useState, useEffect, useMemo } from 'react';
import { Timestamp, collection, query, where, onSnapshot, getDocs } from 'firebase/firestore';
import PackageList from '../ui/PackageList';
import { appId, db } from '../../App';

// --- CONSTANTES ---
const STATUS_OPTIONS = ["Enviado", "Entregado", "Anulado", "Devolución"];

// --- COMPONENTE PARA CABECERAS ORDENABLES ---
const SortableHeader = ({ children, columnKey, sortCriteria, setSortCriteria }) => {
    const currentSort = sortCriteria || '';
    const isSorted = currentSort.startsWith(columnKey);
    const isAsc = isSorted && currentSort.endsWith('Asc');
    const isDesc = isSorted && !isAsc;

    const handleClick = () => {
        if (!setSortCriteria) return;
        if (isSorted) {
            setSortCriteria(isAsc ? `${columnKey}Desc` : `${columnKey}Asc`);
        } else {
            setSortCriteria(columnKey.includes('Date') ? `${columnKey}Desc` : `${columnKey}Asc`);
        }
    };

    return (
        <th className="group text-left p-3 font-semibold text-gray-600 text-xs uppercase tracking-wider cursor-pointer" onClick={handleClick}>
            <div className="flex items-center">
                <span>{children}</span>
                <div className="ml-1.5 flex flex-col">
                    <svg xmlns="http://www.w3.org/2000/svg" className={`h-3 w-3 -mb-1 transition-colors ${isAsc ? 'text-gray-900' : 'text-gray-300 group-hover:text-gray-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" /></svg>
                    <svg xmlns="http://www.w3.org/2000/svg" className={`h-3 w-3 -mt-1 transition-colors ${isDesc ? 'text-gray-900' : 'text-gray-300 group-hover:text-gray-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                </div>
            </div>
        </th>
    );
};

// --- FUNCIONES AUXILIARES ---
const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = date instanceof Timestamp ? date.toDate() : new Date(date);
    if (isNaN(d.getTime())) return 'Fecha Inválida';
    return d.toLocaleDateString('es-GT', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatDateForCSV = (date) => {
    if (!date) return '';
    const d = date instanceof Timestamp ? date.toDate() : new Date(date);
    if (isNaN(d.getTime())) return '';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
};

const exportToCSV = (dataToExport, showModalFn) => {
    if (dataToExport.length === 0) {
        showModalFn("Exportar CSV", "No hay datos para exportar con los filtros actuales.");
        return;
    }
    const headersForCSV = ["# Pedido", "Fecha Envío", "Mensajero", "Valor Producto", "Costo Envío", "Gastos", "Total Paquete", "Total Neto", "Estatus", "Forma de Pago", "Fecha Entrega", "Notas", "Autorización"];
    const csvRows = [headersForCSV.join(";")];
    dataToExport.forEach(item => {
        const rowData = {
            "# Pedido": item.orderId, "Fecha Envío": formatDateForCSV(item.shipDate), "Mensajero": item.courierName,
            "Valor Producto": (item.productValue || 0).toFixed(2), "Costo Envío": (item.shippingCost || 0).toFixed(2), "Gastos": (item.expenses || 0).toFixed(2),
            "Total Paquete": ((parseFloat(item.productValue) || 0) + (parseFloat(item.shippingCost) || 0)).toFixed(2),
            "Total Neto": (((parseFloat(item.productValue) || 0) + (parseFloat(item.shippingCost) || 0)) - (parseFloat(item.expenses) || 0)).toFixed(2),
            "Estatus": item.status, "Forma de Pago": item.paymentMethod, "Fecha Entrega": formatDateForCSV(item.deliveryDate),
            "Notas": item.notes, "Autorización": item.authorizationCode,
        };
        const row = headersForCSV.map(header => `"${String(rowData[header] ?? '').replace(/"/g, '""')}"`);
        csvRows.push(row.join(";"));
    });
    const csvString = csvRows.join("\r\n");
    const blob = new Blob(["\uFEFF" + csvString], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `reporte_mensajero_${new Date().toISOString().slice(0,10)}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};


function ControlMensajeroModule({ showModal, permissions, openFormForEdit }) {
    const [packages, setPackages] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [filters, setFilters] = useState({ courier: '', year: '', month: '', status: '' });
    const [sortCriteria, setSortCriteria] = useState('shipDateAsc');
    const [searchTerm, setSearchTerm] = useState('');
    
    const [availableCouriers, setAvailableCouriers] = useState([]);
    const [availableYears, setAvailableYears] = useState([]);
    const [availableMonths, setAvailableMonths] = useState({});

    useEffect(() => {
        const fetchFilterOptions = async () => {
            const packagesCollectionPath = `artifacts/${appId}/packages`;
            const q = query(collection(db, packagesCollectionPath));
            const querySnapshot = await getDocs(q);

            const couriersSet = new Set();
            const yearMonthMap = {};

            querySnapshot.forEach(doc => {
                const pkg = doc.data();
                if (pkg.courierName) couriersSet.add(pkg.courierName);
                if (pkg.shipDate?.toDate) {
                    const dateObj = pkg.shipDate.toDate();
                    const year = dateObj.getFullYear();
                    const month = dateObj.getMonth();
                    if (!yearMonthMap[year]) {
                        yearMonthMap[year] = new Set();
                    }
                    yearMonthMap[year].add(month);
                }
            });

            const finalYears = Object.keys(yearMonthMap).sort((a, b) => b - a);
            const finalMonths = {};
            for (const year in yearMonthMap) {
                finalMonths[year] = Array.from(yearMonthMap[year]).sort((a, b) => a - b);
            }

            setAvailableCouriers(Array.from(couriersSet).sort());
            setAvailableYears(finalYears);
            setAvailableMonths(finalMonths);
        };
        fetchFilterOptions();
    }, []);

    useEffect(() => {
        if (!filters.courier || !filters.year || filters.month === '') {
            setPackages([]);
            return;
        }

        setIsLoading(true);
        const startDate = new Date(filters.year, filters.month, 1);
        const endDate = new Date(filters.year, parseInt(filters.month) + 1, 1);

        const packagesCollectionPath = `artifacts/${appId}/packages`;
        let conditions = [
            where('courierName', '==', filters.courier),
            where('shipDate', '>=', startDate),
            where('shipDate', '<', endDate)
        ];

        // --- CORRECCIÓN PARA EL FILTRO DE ESTADO ---
        if (filters.status) {
            // Se crea un array con las variaciones más comunes del estado para asegurar que la búsqueda encuentre coincidencias
            // sin importar si se guardó en mayúsculas, minúsculas o con mayúscula inicial.
            const statusVariations = [
                filters.status, // "Enviado"
                filters.status.toLowerCase(), // "enviado"
                filters.status.toUpperCase() // "ENVIADO"
            ];
            // La consulta de Firestore ahora buscará paquetes cuyo estado esté en cualquiera de estas variaciones.
            conditions.push(where('status', 'in', statusVariations));
        }
        
        const q = query(collection(db, packagesCollectionPath), ...conditions);

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const packagesData = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                shipDate: doc.data().shipDate?.toDate ? doc.data().shipDate.toDate() : null,
                deliveryDate: doc.data().deliveryDate?.toDate ? doc.data().deliveryDate.toDate() : null,
            }));
            setPackages(packagesData);
            setIsLoading(false);
        }, (error) => {
            console.error("Error al cargar paquetes por mensajero:", error);
            showModal("Error de Carga", "No se pudieron cargar los paquetes para esta selección. Es posible que falte un índice en la base de datos.");
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, [filters, showModal]);

    const filteredAndSortedPackages = useMemo(() => {
        let tempPackages = [...packages];

        if (searchTerm.trim() !== '') {
            const lowerCaseTerm = searchTerm.toLowerCase().trim();
            tempPackages = tempPackages.filter(pkg => {
                const shipDateStr = pkg.shipDate ? formatDate(pkg.shipDate) : '';
                const deliveryDateStr = pkg.deliveryDate ? formatDate(pkg.deliveryDate) : '';
                return [pkg.orderId, pkg.status, pkg.paymentMethod, shipDateStr, deliveryDateStr]
                    .some(field => String(field).toLowerCase().includes(lowerCaseTerm));
            });
        }

        tempPackages.sort((a, b) => {
            const field = sortCriteria.replace(/Desc|Asc/, '');
            const direction = sortCriteria.endsWith('Desc') ? -1 : 1;
            const valA = a[field];
            const valB = b[field];
            if (valA === null || valA === undefined) return 1 * direction;
            if (valB === null || valB === undefined) return -1 * direction;
            if (valA instanceof Date && valB instanceof Date) return (valA.getTime() - valB.getTime()) * direction;
            return String(valA).localeCompare(String(valB)) * direction;
        });

        return tempPackages;
    }, [packages, searchTerm, sortCriteria]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        if (name === 'year') {
            setFilters(prev => ({ ...prev, year: value, month: '' }));
        } else {
            setFilters(prev => ({ ...prev, [name]: value }));
        }
    };

    if (!permissions?.CONTROL_MENSAJERO?.view) {
        return <div className="text-center py-10 bg-red-50 border border-red-200 rounded-lg shadow"><h2 className="text-2xl font-semibold text-red-700">Acceso Denegado</h2></div>;
    }

    return (
        <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6">Control de Pedidos por Mensajero</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6 items-end">
                <div>
                    <label htmlFor="courierSelectCM" className="block text-sm font-medium text-gray-700 mb-1">Mensajero:</label>
                    <select id="courierSelectCM" name="courier" value={filters.courier} onChange={handleFilterChange} className="input-field bg-white w-full">
                        <option value="">-- Selecciona Mensajero --</option>
                        {availableCouriers.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                </div>
                <div>
                    <label htmlFor="yearSelectCM" className="block text-sm font-medium text-gray-700 mb-1">Año (Envío):</label>
                    <select id="yearSelectCM" name="year" value={filters.year} onChange={handleFilterChange} className="input-field bg-white w-full">
                        <option value="">-- Selecciona Año --</option>
                        {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                </div>
                <div>
                    <label htmlFor="monthSelectCM" className="block text-sm font-medium text-gray-700 mb-1">Mes (Envío):</label>
                    <select id="monthSelectCM" name="month" value={filters.month} onChange={handleFilterChange} className="input-field bg-white w-full" disabled={!filters.year}>
                        <option value="">-- Selecciona Mes --</option>
                        {filters.year && (availableMonths[filters.year] || []).map(monthIndex => (
                            <option key={monthIndex} value={monthIndex}>
                                {new Date(filters.year, monthIndex, 1).toLocaleString('es-GT', { month: 'long' })}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label htmlFor="statusSelectCM" className="block text-sm font-medium text-gray-700 mb-1">Estado:</label>
                    <select id="statusSelectCM" name="status" value={filters.status} onChange={handleFilterChange} className="input-field bg-white w-full">
                        <option value="">-- Todos los Estados --</option>
                        {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                </div>
                <div className="lg:col-span-1">
                    <label htmlFor="searchCM" className="block text-sm font-medium text-gray-700 mb-1">Búsqueda Rápida:</label>
                    <input id="searchCM" type="text" placeholder="Buscar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="input-field w-full"/>
                </div>
            </div>

            {(filteredAndSortedPackages.length > 0 && filters.courier && filters.year && filters.month !== '') && (
                <div className="my-4 flex justify-end">
                    <button onClick={() => exportToCSV(filteredAndSortedPackages, showModal)} className="btn-success text-sm">
                        Descargar Vista Actual (CSV)
                    </button>
                </div>
            )}

            {isLoading && <div className="text-center py-10"><p className="animate-pulse">Cargando paquetes...</p></div>}
            
            {!isLoading && (
                (filters.courier && filters.year && filters.month !== '') ? (
                    filteredAndSortedPackages.length > 0 ? (
                        <PackageList 
                            packages={filteredAndSortedPackages} 
                            onEdit={(pkg) => openFormForEdit(pkg, true)} 
                            showEditButton={permissions.CONTROL_MENSAJERO?.edit} 
                            onDelete={null} 
                            sortCriteria={sortCriteria}
                            setSortCriteria={setSortCriteria}
                            SortableHeader={SortableHeader}
                        />
                    ) : (
                        <p className="text-gray-600 text-center py-4">No hay paquetes para la selección actual.</p>
                    )
                ) : (
                    <p className="text-gray-600 text-center py-4">Por favor, selecciona un mensajero, año y mes para ver los pedidos.</p>
                )
            )}
        </div>
    );
}

export default ControlMensajeroModule;